import React, { Fragment, useEffect, useState } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  InputGroup,
  InputGroupAddon,
  FormGroup,
  FormCheck,
  Label,
  Row,
  Col,
  Navbar,
  Nav,
  Spinner,
} from "reactstrap";
import { useForm, useFieldArray } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useDispatch } from "react-redux";
import { X, Search } from "react-feather";
import countriesByContinent from "../../../utils/countries";

function CountriesModalWindow(props) {
  const { open, onClose, initialSelectedCountries = [], onSelectCountries, append, watch, setValue, control, errors, reset } = props;

  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredCountries, setFilteredCountries] = useState({});

  const {
    fields: selectedCountriesFields,
    append: appendCountry,
    remove: removeCountry,
  } = useFieldArray({
    name: "selectedCountries",
    control,
  });

  const selectedCountries = watch("selectedCountries");

  useEffect(() => {
    setFilteredCountries(countriesByContinent);
  }, []);

  useEffect(() => {
    if (searchTerm === "") {
      setFilteredCountries(countriesByContinent);
      return;
    }

    const filtered = {};
    Object.keys(countriesByContinent).forEach((continent) => {
      const continentCountries = countriesByContinent[continent].filter((country) => country.name.toLowerCase().includes(searchTerm.toLowerCase()));

      if (continentCountries.length > 0) {
        filtered[continent] = continentCountries;
      }
    });

    setFilteredCountries(filtered);
  }, [searchTerm]);

  useEffect(() => {
    if (open && initialSelectedCountries && initialSelectedCountries.length > 0) {
      const selectedCountriesWithContinent = Object.entries(countriesByContinent).flatMap(([continent, countries]) =>
        countries
          .filter((country) => initialSelectedCountries.includes(country.code))
          .map((country) => ({
            ...country,
            continent, // add continent back
          }))
      );

      reset({
        selectedCountries: selectedCountriesWithContinent || [],
      });
    }
  }, [open, initialSelectedCountries, reset]);

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleSelectAll = () => {
    const allCountries = [];
    Object.keys(countriesByContinent).forEach((continent) => {
      countriesByContinent[continent].forEach((country) => {
        allCountries.push({
          code: country.code,
          name: country.name,
          phone: country.phone,
          continent,
        });
      });
    });
    setValue("selectedCountries", allCountries);
  };

  const handleUnselectAll = () => {
    setValue("selectedCountries", []);
  };

  const isCountrySelected = (countryCode) => {
    return selectedCountries.some((country) => country.code === countryCode);
  };

  const handleToggleCountry = (country, continent) => {
    const currentIndex = selectedCountries.findIndex((item) => item.code === country.code);

    if (currentIndex === -1) {
      appendCountry({
        code: country.code,
        name: country.name,
        phone: country.phone,
        continent,
      });
    } else {
      removeCountry(currentIndex);
    }
  };

  const handleSelectContinent = (continent) => {
    const continentCountries = countriesByContinent[continent].map((country) => ({
      code: country.code,
      name: country.name,
      phone: country.phone,
      continent,
    }));

    const newCountries = continentCountries.filter((country) => !selectedCountries.some((selected) => selected.code === country.code));

    setValue("selectedCountries", [...selectedCountries, ...newCountries]);
  };

  const handleUnselectContinent = (continent) => {
    const filteredAllCountries = selectedCountries.filter((country) => country.continent !== continent);
    setValue("selectedCountries", filteredAllCountries);
  };

  return (
    <Fragment>
      <Row className="mb-3">
        <Col md={6} className="mb-3 mb-md-0">
          <InputGroup>
            <Input placeholder="Search country" value={searchTerm} onChange={handleSearchChange} />
            <InputGroupAddon addonType="append">
              <Button color="secondary">
                <Search size={18} />
              </Button>
            </InputGroupAddon>
          </InputGroup>
        </Col>

        <Col md={6}>
          <Row>
            <Col md={6}>
              <FormGroup check>
                <Input type="checkbox" onChange={handleSelectAll} id="select-all" />
                <Label check for="select-all">
                  Select All
                </Label>
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup check>
                <Input type="checkbox" onChange={handleUnselectAll} id="unselect-all" />
                <Label check for="unselect-all">
                  Unselect All
                </Label>
              </FormGroup>
            </Col>
          </Row>
        </Col>
      </Row>

      {errors.selectedCountries && (
        <Row className="mb-3">
          <Col>
            <div className="text-danger">{errors.selectedCountries.message}</div>
          </Col>
        </Row>
      )}

      <div className="country-grid" style={{ maxHeight: "350px", overflowY: "auto" }}>
        {Object.keys(filteredCountries).length === 0 ? (
          <div>No countries match your search</div>
        ) : (
          Object.keys(filteredCountries).map((continent) => (
            <div key={continent} className="mb-4">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h6 className="fw-bold mb-0">{continent}</h6>
                <div>
                  <Button size="sm" color="link" onClick={() => handleSelectContinent(continent)} className="me-2">
                    Select All
                  </Button>
                  <Button size="sm" color="link" onClick={() => handleUnselectContinent(continent)}>
                    Clear
                  </Button>
                </div>
              </div>
              <hr className="mb-3" />
              <Row>
                {filteredCountries[continent].map((country) => (
                  <Col md={4} sm={6} xs={12} key={country.code}>
                    <FormGroup check>
                      <Input
                        type="checkbox"
                        checked={isCountrySelected(country.code)}
                        onChange={() => handleToggleCountry(country, continent)}
                        id={`country-${country.code}`}
                      />
                      <Label check for={`country-${country.code}`}>
                        {country.name} ({country.code})
                      </Label>
                    </FormGroup>
                  </Col>
                ))}
              </Row>
            </div>
          ))
        )}
      </div>
    </Fragment>
  );
}

export default CountriesModalWindow;
